













































































// @ts-ignore
import v8n from 'v8n';
import { Component, Mixins } from 'vue-property-decorator';
import { EmailVerification, EmailVerificationResend } from '@/api';
import ResendCooldown from '@/mixins/ResendCooldown';

type State = 'verified' | 'expired' | 'invalid';

/**
 * Landing page for the link in a verification email:
 * `{FRONTEND_URL}/verify-email/?token=...`.
 *
 * Needs no WordPress page - the theme serves the built SPA for any unmatched
 * path and the router takes it from there.
 */
@Component
export default class VerifyEmail extends Mixins(ResendCooldown) {
  loading: boolean = true;

  state: State = 'invalid';

  email: string = '';

  valid: boolean = false;

  resending: boolean = false;

  message: string = '';

  messageType: string = 'info';

  emailRules = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'Email address required',
    (v: string) =>
      v8n()
        .pattern(/\S+@\S+\.\S+/)
        .test(v) || 'Email address must be valid',
  ];

  get token(): string {
    const token = this.$route.query.token;
    return Array.isArray(token) ? token[0] || '' : token || '';
  }

  async mounted() {
    if (!this.token) {
      this.state = 'invalid';
      this.loading = false;
      return;
    }
    try {
      await EmailVerification.verify(this.token);
      this.state = 'verified';
    } catch (e) {
      // `expired` is the only failure a resend can fix. `invalid` covers a
      // tampered link and - far more commonly - one that has already been
      // used, since verifying clears the nonce it carries. Do not present that
      // as a failure: the user is most likely already verified.
      const errors = e.response ? e.response.errors : [];
      const code = errors.length ? errors[0].code : null;
      this.state = code === 'expired' ? 'expired' : 'invalid';
    } finally {
      this.loading = false;
    }
  }

  async resend() {
    if (this.cooldownActive) {
      return;
    }
    this.resending = true;
    this.message = '';
    try {
      await EmailVerificationResend.request(this.email);
      // Generic by design - the endpoint reveals nothing about whether the
      // address exists or is already verified, so neither does this.
      this.messageType = 'success';
      this.message =
        'If that address needs confirming, a new link is on its way.';
    } catch (e) {
      const status = e.response ? e.response.status : null;
      if (status === 429) {
        this.messageType = 'warning';
        this.message = 'Please wait a moment before requesting another email.';
      } else {
        this.messageType = 'error';
        this.message = 'That did not work just now. Please try again shortly.';
      }
    } finally {
      this.resending = false;
      // Every outcome starts the countdown, including the failures - a request
      // that errored is exactly the one a user retries in a hurry.
      this.startCooldown();
    }
  }
}
